import React, { useRef, useState } from "react";
import "./NewAddressForm.css";
import { InputText } from "primereact/inputtext";
import { Field, FormSpy } from "react-final-form";
import { Dropdown } from "primereact/dropdown";
import { Tooltip } from "primereact/tooltip";
import { buildAddressStringFromObject } from "../../services/formatting";
import { streetAddress2Message } from "../../services/constants";

const NewAddressForm = ({
  formPrefix,
  initial = {},
  addressChoices = [],
  creatable = false,
  required = false,
  hideWestVirginia = false,
  states,
  setTraId = () => {}
}) => {
  const [isPrefilled, setIsPrefilled] = useState(false);
  const [allowAddress2, setAllowAddress2] = useState(false);
  const containerRef = useRef(null);
  const streetAddressRef = useRef(null);

  const isTra = [
    "titleRemittanceAddress",
    "acquiringPartyMailingAddress"
  ].includes(formPrefix);

  const isFormFieldValid = meta => !!(meta.touched && meta.error);
  const getFormErrorMessage = meta => {
    return (
      isFormFieldValid(meta) && <small className="p-error">{meta.error}</small>
    );
  };

  const addresses = creatable
    ? [
        {
          value: "new",
          name: "Submit New Address"
        },
        ...addressChoices
      ]
    : addressChoices;

  // returns the address string for a piece of the address form
  const getStreetAddress = (form, input, addressPart) => {
    const prefillId = form.getFieldState(`${formPrefix}.prefilledAddress`)
      ?.value?.titleRemittanceAddressId;
    let prefill = null;
    const inputValue = input.value;
    if (addressPart === "all") {
      if (prefillId && prefillId !== "new") {
        const prefillAddress = addressChoices.filter(
          address => address?.titleRemittanceAddressId === prefillId
        )[0];

        return buildAddressStringFromObject({
          streetAddress: prefillAddress?.streetAddress,
          streetAddress2: prefillAddress?.streetAddress2,
          city: prefillAddress?.city,
          state: prefillAddress?.state,
          zipCode: prefillAddress?.zipCode
        });
      } else {
        if (isPrefilled) {
          setIsPrefilled(false);
        }
        const streetAddress = form.getFieldState(`${formPrefix}.streetAddress`)
          ?.value;
        const streetAddress2 = form.getFieldState(
          `${formPrefix}.streetAddress2`
        )?.value;
        const city = form.getFieldState(`${formPrefix}.city`)?.value;
        const state = form.getFieldState(`${formPrefix}.state`)?.value;
        const zipCode = form.getFieldState(`${formPrefix}.zipCode`)?.value;
        if (!streetAddress || !city || !state || !zipCode) {
          return "";
        } else {
          return buildAddressStringFromObject({
            streetAddress,
            streetAddress2,
            city,
            state,
            zipCode
          });
        }
      }
    }
    if (prefillId == null) {
      if (isPrefilled) {
        setIsPrefilled(false);
      }
      return inputValue;
    }
    if (prefillId !== "new" && prefillId != null && isTra) {
      setTraId(prefillId);
    }
    if (prefillId === "new" && addressPart === "state") {
      if (isPrefilled) {
        setIsPrefilled(false);
      }
      return inputValue;
    } else if (prefillId === "new") {
      setIsPrefilled(false);
      return inputValue;
    } else if (prefillId && addressPart === "state") {
      const statePrefill = addressChoices.filter(
        address => address?.titleRemittanceAddressId === prefillId
      )[0]?.stateId;
      prefill = states?.filter(state => state?.stateId === statePrefill)[0];
      setIsPrefilled(true);
    } else if (prefillId) {
      prefill = addressChoices.filter(
        address => address?.titleRemittanceAddressId === prefillId
      )[0][addressPart];
      setIsPrefilled(true);
    }

    return prefill ?? inputValue;
  };

  const handleDefaultAddress = (value, form) => {
    if (value === "new") {
      form.change(`${formPrefix}.streetAddress`, "");
      form.change(`${formPrefix}.streetAddress2`, "");
      form.change(`${formPrefix}.city`, "");
      form.change(`${formPrefix}.state`, "");
      form.change(`${formPrefix}.zipCode`, "");
    }
    form.change(`${formPrefix}.streetAddress`, value?.streetAddress);
    form.change(`${formPrefix}.streetAddress2`, value?.streetAddress2 || " ");
    form.change(`${formPrefix}.city`, value?.city);
    form.change(`${formPrefix}.state`, value?.state);
    form.change(`${formPrefix}.zipCode`, value?.zipCode);
    if (value?.streetAddress2 == null || value?.streetAddress2 === " ") {
      setAllowAddress2(true);
    } else {
      setAllowAddress2(false);
    }
  };

  return (
    <FormSpy
      subscription={{ values: true }}
      render={({ form }) => (
        <>
          {addressChoices.length > 0 && (
            <Field
              name={`${formPrefix}.prefilledAddress`}
              required={required}
              render={({ input }) => (
                <div className="addressField">
                  <span className="p-float-label">
                    <Dropdown
                      data-triggercollapse={false}
                      options={addresses.map(address => ({
                        label: address.name,
                        value: {
                          titleRemittanceAddressId:
                            address.titleRemittanceAddressId,
                          streetAddress: address.streetAddress,
                          streetAddress2: address.streetAddress2,
                          city: address.city,
                          state: address.state,
                          zipCode: address.zipCode
                        }
                      }))}
                      {...input}
                      onChange={e => {
                        input.onChange(e.value);
                        handleDefaultAddress(e.value, form);
                      }}
                    ></Dropdown>
                    <label htmlFor={input.name}>Existing Addresses</label>
                  </span>
                </div>
              )}
            />
          )}
          <div ref={containerRef} tabIndex="0">
            <Field
              name={`${formPrefix}.streetAddress`}
              ref={streetAddressRef}
              initialValue={initial?.streetAddress}
              required={required}
              render={({ input, meta }) => (
                <div className="addressField">
                  <span className="p-float-label">
                    <InputText
                      id={input.name}
                      {...input}
                      disabled={isPrefilled}
                      value={getStreetAddress(form, input, "streetAddress")}
                      data-triggercollapse={false}
                      maxLength={50}
                    />
                    <label htmlFor={input.name}>Street Address</label>
                  </span>
                  {getFormErrorMessage(meta)}
                </div>
              )}
            />
            <div
              className={"AddressLineCityState address-tooltip"}
              data-pr-tooltip={streetAddress2Message}
              data-pr-position="left"
              data-pr-at="left center"
              data-pr-my="right center"
            >
              <Field
                name={`${formPrefix}.streetAddress2`}
                initialValue={initial?.streetAddress2}
                render={({ input }) => (
                  <div className="halfFieldLeft">
                    <span className="p-float-label">
                      <InputText
                        id={input.name}
                        {...input}
                        disabled={isPrefilled && !allowAddress2}
                        value={getStreetAddress(form, input, "streetAddress2")}
                        data-triggercollapse={false}
                        maxLength={50}
                      />
                      <label htmlFor={input.name}>Street Address 2</label>
                    </span>
                  </div>
                )}
              />
              <Field
                name={`${formPrefix}.city`}
                initialValue={initial?.city}
                required
                render={({ input }) => (
                  <div className="halfField">
                    <span className="p-float-label">
                      <InputText
                        id={input.name}
                        {...input}
                        disabled={isPrefilled}
                        value={getStreetAddress(form, input, "city")}
                        maxLength={30}
                        data-triggercollapse={false}
                      />
                      <label htmlFor={input.name}>City</label>
                    </span>
                  </div>
                )}
              />
            </div>
            <div className={"AddressLineCityState"}>
              <Field
                name={`${formPrefix}.state`}
                initialValue={initial?.stateName}
                required
                render={({ input }) => (
                  <div className="halfFieldLeft">
                    <span className="p-float-label">
                      <Dropdown
                        id={input.name}
                        options={
                          hideWestVirginia
                            ? states?.filter(
                                state => state.stateInitials !== "WV"
                              )
                            : states
                        }
                        optionLabel={"stateName"}
                        {...input}
                        disabled={isPrefilled}
                        value={getStreetAddress(form, input, "state")}
                        data-triggercollapse={false}
                      />
                      <label htmlFor={input.name}>State</label>
                    </span>
                  </div>
                )}
              />
              <Field
                name={`${formPrefix}.zipCode`}
                initialValue={initial?.zipCode}
                required
                render={({ input }) => (
                  <div className="halfField">
                    <span className="p-float-label">
                      <InputText
                        id={input.name}
                        {...input}
                        disabled={isPrefilled}
                        value={getStreetAddress(form, input, "zipCode")}
                        data-triggercollapse={false}
                        maxLength={50}
                      />
                      <label htmlFor={input.name}>Zip Code</label>
                    </span>
                  </div>
                )}
              />
            </div>
          </div>
          <Tooltip target={".address-tooltip"} />
        </>
      )}
    />
  );
};
export default NewAddressForm;
